import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { businessParentChildService } from '../services/businessParentChildService';
import type { BusinessParentChildAssociation } from '../types/businessParentChild';
import spottoLogo from '../assets/spotto-logo.png';

export const BusinessParentChildConfirm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();

  const [association, setAssociation] = useState<BusinessParentChildAssociation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [useParentTPay, setUseParentTPay] = useState(true);
  const [useParentNipForInvoices, setUseParentNipForInvoices] = useState(true);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('businessParentChild.invalidLink'));
      setIsLoading(false);
      return;
    }

    loadAssociation();
  }, [token, t]);

  const loadAssociation = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await businessParentChildService.getAssociationByToken(token!);
      setAssociation(data);

      if (data.status !== 'Pending') {
        setError(t('businessParentChild.alreadyProcessed'));
      }
    } catch (err) {
      console.error('Failed to load association:', err);
      setError(t('businessParentChild.invalidOrExpiredLink'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!token || !association) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await businessParentChildService.confirmAssociation({
        token,
        confirm: true,
        useParentTPay,
        useParentNipForInvoices,
      });

      setSuccess(t('businessParentChild.confirmSuccess'));
    } catch (err) {
      console.error('Failed to confirm association:', err);
      setError(err instanceof Error ? err.message : t('businessParentChild.confirmError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token || !association) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await businessParentChildService.confirmAssociation({
        token,
        confirm: false,
        rejectionReason: rejectionReason.trim() || undefined,
      });

      setSuccess(t('businessParentChild.rejectSuccess'));
    } catch (err) {
      console.error('Failed to reject association:', err);
      setError(err instanceof Error ? err.message : t('businessParentChild.rejectError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-ink-secondary">{t('businessParentChild.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state (no association loaded)
  if (error && !association) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={spottoLogo} alt="Spotto Logo" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">{t('app.name')}</h1>
          </div>

          <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 shadow-xl">
            <div className="text-center">
              <div className="text-6xl mb-4">😕</div>
              <h2 className="text-xl font-bold text-white mb-2">{t('businessParentChild.errorTitle')}</h2>
              <p className="text-brand-warm-deep">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={spottoLogo} alt="Spotto Logo" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">{t('app.name')}</h1>
          </div>

          <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-confirm/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-brand-confirm-deep" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t('businessParentChild.successTitle')}</h2>
              <p className="text-brand-confirm-deep">{success}</p>
              <p className="text-ink-secondary text-sm mt-4">{t('businessParentChild.canCloseWindow')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={spottoLogo} alt="Spotto Logo" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">{t('app.name')}</h1>
          <p className="text-ink-secondary">SCHEDULE - SPORT - PROGRESS</p>
        </div>

        {/* Main Card */}
        <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-2">{t('businessParentChild.title')}</h2>
          <p className="text-ink-secondary mb-6">{t('businessParentChild.description')}</p>

          {/* Association Info */}
          <div className="bg-surface-app-elevated rounded-xl p-4 mb-6">
            {/* Parent Business (Your Business - the one confirming) */}
            <div className="flex items-center gap-4 mb-4">
              {association?.parentBusinessAvatarUrl ? (
                <img
                  src={association.parentBusinessAvatarUrl}
                  alt={association.parentBusinessName}
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                <div className="w-14 h-14 bg-brand-primary rounded-full flex items-center justify-center text-xl text-white font-bold">
                  {association?.parentBusinessName?.charAt(0) || 'P'}
                </div>
              )}
              <div>
                <p className="text-ink-secondary text-xs mb-1">{t('businessParentChild.yourBusiness')}</p>
                <p className="text-white font-semibold">{association?.parentBusinessName}</p>
              </div>
            </div>

            <div className="border-t border-border-strong pt-4">
              <p className="text-ink-secondary text-sm mb-1">{t('businessParentChild.requestingBusiness')}</p>
              <div className="flex items-center gap-3 mt-2">
                {association?.childBusinessAvatarUrl ? (
                  <img
                    src={association.childBusinessAvatarUrl}
                    alt={association.childBusinessName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-brand-confirm rounded-full flex items-center justify-center text-lg text-white font-bold">
                    {association?.childBusinessName?.charAt(0) || 'C'}
                  </div>
                )}
                <p className="text-white font-semibold text-lg">{association?.childBusinessName}</p>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-brand-warm/10 border border-brand-warm/30 rounded-lg p-4 mb-6">
              <p className="text-brand-warm-deep text-sm">{error}</p>
            </div>
          )}

          {!showRejectForm ? (
            <>
              {/* Permission Options */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3">{t('businessParentChild.selectPermissions')}</h3>
                <div className="space-y-3">
                  {/* Use Parent TPay */}
                  <label
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      useParentTPay
                        ? 'bg-brand-primary/15 border-brand-primary-light shadow-lg shadow-brand-primary/20'
                        : 'bg-surface-app-elevated border-border-strong hover:border-border-strong hover:bg-surface-app-elevated'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      useParentTPay
                        ? 'border-brand-primary-light bg-brand-primary/50'
                        : 'border-border-strong'
                    }`}>
                      {useParentTPay && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${useParentTPay ? 'text-brand-primary' : 'text-white'}`}>
                        {t('businessParentChild.useParentTPay')}
                      </p>
                      <p className="text-ink-secondary text-sm">{t('businessParentChild.useParentTPayDesc')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={useParentTPay}
                      onChange={() => setUseParentTPay(!useParentTPay)}
                      className="sr-only"
                    />
                  </label>

                  {/* Use Parent NIP for Invoices */}
                  <label
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      useParentNipForInvoices
                        ? 'bg-brand-primary/15 border-brand-primary-light shadow-lg shadow-brand-primary/20'
                        : 'bg-surface-app-elevated border-border-strong hover:border-border-strong hover:bg-surface-app-elevated'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      useParentNipForInvoices
                        ? 'border-brand-primary-light bg-brand-primary/50'
                        : 'border-border-strong'
                    }`}>
                      {useParentNipForInvoices && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${useParentNipForInvoices ? 'text-brand-primary' : 'text-white'}`}>
                        {t('businessParentChild.useParentNip')}
                      </p>
                      <p className="text-ink-secondary text-sm">{t('businessParentChild.useParentNipDesc')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={useParentNipForInvoices}
                      onChange={() => setUseParentNipForInvoices(!useParentNipForInvoices)}
                      className="sr-only"
                    />
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={isSubmitting || association?.status !== 'Pending'}
                  className="w-full bg-brand-primary text-white py-3 px-6 rounded-xl font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? t('businessParentChild.confirming') : t('businessParentChild.confirmButton')}
                </button>

                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={isSubmitting || association?.status !== 'Pending'}
                  className="w-full bg-surface-app-elevated text-ink-primary py-3 px-6 rounded-xl font-semibold hover:bg-surface-card-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('businessParentChild.rejectButton')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Reject Form */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3">{t('businessParentChild.rejectTitle')}</h3>
                <p className="text-ink-secondary text-sm mb-4">{t('businessParentChild.rejectDescription')}</p>

                <div>
                  <label className="block text-sm font-medium text-ink-primary mb-2">
                    {t('businessParentChild.rejectionReason')}
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-4 py-3 bg-surface-app-elevated border border-border-strong rounded-xl text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    placeholder={t('businessParentChild.rejectionReasonPlaceholder')}
                    rows={3}
                  />
                </div>
              </div>

              {/* Reject Actions */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleReject}
                  disabled={isSubmitting}
                  className="w-full bg-brand-warm text-white py-3 px-6 rounded-xl font-semibold hover:bg-brand-warm-deep disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? t('businessParentChild.rejecting') : t('businessParentChild.confirmReject')}
                </button>

                <button
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectionReason('');
                  }}
                  disabled={isSubmitting}
                  className="w-full bg-surface-app-elevated text-ink-primary py-3 px-6 rounded-xl font-semibold hover:bg-surface-card-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('businessParentChild.goBack')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
